import React from "react";
import { motion } from "framer-motion";
import {
  Image,
  Video,
  Mountain,
  Users,
  Terminal,
  Layers,
  Monitor,
  Workflow,
} from "lucide-react";

const deliverables = [
  {
    icon: Image,
    title: "AI-VFX Keyframes",
    desc: "High-detail cinematic frames for creative approval and VFX planning.",
  },
  {
    icon: Video,
    title: "Sequence Previz",
    desc: "Shot flow, staging, action beats, and edit-friendly visual placeholders.",
  },
  {
    icon: Mountain,
    title: "Set Extension Briefs",
    desc: "Environment replacement, depth logic, lighting match, and physical scale notes.",
  },
  {
    icon: Users,
    title: "Character Look Books",
    desc: "Identity-locked sheets, costume logic, texture references, and continuity views.",
  },
  {
    icon: Terminal,
    title: "Prompt Systems",
    desc: "Engine-specific prompts for MidJourney, Kling, Veo, Seedance, and Nano Banana workflows.",
  },
  {
    icon: Layers,
    title: "VFX Breakdowns",
    desc: "Production-friendly breakdown notes connecting creative intent to technical execution.",
  },
  {
    icon: Monitor,
    title: "Edit Placeholders",
    desc: "4K live-action-style visual placeholders for editorial and discussion.",
  },
  {
    icon: Workflow,
    title: "Workflow Support",
    desc: "AI pipeline planning for fast ideation under film production pressure.",
  },
];

export default function DeliverablesSection() {
  return (
    <section
      id="deliverables"
      data-testid="deliverables-section"
      className="py-24 sm:py-32 px-6 sm:px-12"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="section-number mb-4">06 &middot; Deliverables</p>
          <h2
            className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-none text-white max-w-3xl"
            data-testid="deliverables-heading"
          >
            What the website{" "}
            <span className="font-serif-accent text-amber-400">
              sells clearly.
            </span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-neutral-400 leading-relaxed max-w-2xl">
            Use this section for client-facing work, production collaborations,
            studio pitch discussions, or internal film presentation links.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-16">
          {deliverables.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{
                duration: 0.5,
                delay: i * 0.06,
                ease: [0.16, 1, 0.3, 1],
              }}
              data-testid={`deliverable-card-${i}`}
              className="glass-surface glass-surface-hover rounded-2xl p-6 transition-all duration-500 group"
            >
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center mb-4 group-hover:bg-amber-500/20 transition-colors duration-500">
                <item.icon size={20} className="text-amber-500" />
              </div>
              <h3 className="font-heading text-base tracking-tight text-white font-medium mb-2">
                {item.title}
              </h3>
              <p className="text-sm text-neutral-500 leading-relaxed">
                {item.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
